import { Flex, HStack, Stack, Text } from '@chakra-ui/react';

import type { ExpertFormData } from '../../types';
import { ExpertResource, IntegrationSourceType } from '@/client/services/api/graphql/gql/graphql';
import FileTypeIcon from '../FileTypeIcon';
import WarmNeutralEllipsisMenu from '@/client/components/shared/menus/WarmNeutralEllipsisMenu';
import { useExpertsStore } from '../../../expertsStore';
import { useFormContext } from 'react-hook-form';
import { useToastStore } from '@/client/services/state/toastStore';
import { useTranslation } from 'react-i18next';

interface LocalUploadFileListProps {
  /** Maximum number of files to render in the list */
  renderLimit?: number;
}

export default function LocalUploadFileList({ renderLimit }: LocalUploadFileListProps) {
  const { t } = useTranslation();
  const { setToast } = useToastStore();
  const { isEditingExpert } = useExpertsStore();
  const { watch, setValue } = useFormContext<ExpertFormData>();

  const { resources } = watch();

  const handleDownload = (resource: ExpertResource) => {
    if (!resource.downloadLink) {
      setToast({
        show: true,
        status: 'error',
        title: t('admin.aiAgent.experts.form.integrations.localUpload.unableToDownload'),
      });

      return;
    }

    fetch(resource.downloadLink)
      .then((response) => response.blob())
      .then((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = resource.extensionName;
        document.body.appendChild(a);
        a.click();
        a.remove();
      })
      .catch((err) => console.error('Download failed', err));
  };

  const handleRemoveResource = (resource: ExpertResource) =>
    setValue(
      'resources',
      resources.filter((r) => r.path !== resource.path),
      {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      },
    );

  const filesToRender = renderLimit ? resources.filter(resource => resource.source === IntegrationSourceType.Upload).slice(0, renderLimit) : resources.filter(resource => resource.source === IntegrationSourceType.Upload);

  return (
    <Stack spacing={0}>
      {filesToRender.map((resource) => (
        <Flex key={resource.path} justifyContent="space-between" alignItems="center">
          <HStack spacing={2}>
            <FileTypeIcon type={resource.extensionType} />

            <Text fontSize="14px" fontWeight={500} color="baseBlack">
              {resource.extensionName}
            </Text>
          </HStack>

          <WarmNeutralEllipsisMenu
            iconOrientation="horizontal"
            options={[
              {
                label: t('admin.aiAgent.experts.form.integrations.localUpload.download'),
                isHidden: !resource.downloadLink,
                onClick: () => handleDownload(resource),
              },
              {
                label: t('admin.aiAgent.experts.form.integrations.localUpload.remove'),
                isHidden: !isEditingExpert,
                onClick: () => handleRemoveResource(resource),
              },
            ]}
          />
        </Flex>
      ))}
    </Stack>
  );
}
